import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, Download, Edit3, FileText, Truck, Store } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import OrderFulfillmentModal from '../../components/OrderFulfillmentModal';
import { prepSortItems } from '../../utils/itemSorting';
import { ShipmentInfoModal } from '../../components/admin/ShipmentInfoModal';
import { OrderProfitBadge } from '../../components/admin/OrderProfitBadge';
import { WhatsAppNotifyButton } from '../../components/admin/WhatsAppNotifyButton';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminOrders = () => {
  const { isAdmin, isStaff } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  // Default filter: focus on what needs attention — pending + processing.
  // The user can switch to other statuses or "Toutes" via the chips.
  const [statusFilter, setStatusFilter] = useState('active');
  const [exporting, setExporting] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [shipmentTarget, setShipmentTarget] = useState(null); // order id
  const [submittingShipment, setSubmittingShipment] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/orders`);
      setOrders(response.data);
    } catch (error) {
      // Error shown via loading=false + null data check in UI
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isStaff) {
      navigate('/connexion');
      return;
    }
    fetchOrders();
  }, [isStaff, navigate, fetchOrders]);

  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(
        `${API_URL}/api/orders/export/csv${params}`,
        {  responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `commandes_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch (error) {
      // handled by toast
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const updateStatus = async (orderId, newStatus, shipment = null) => {
    try {
      // IMPORTANT: only send a body when we actually have shipment info.
      // Sending `{}` makes FastAPI try to validate it as `ShipmentInfo` and
      // reject with 422 because `shipping_method` is required.
      const res = shipment
        ? await axios.put(`${API_URL}/api/orders/${orderId}/status?status=${newStatus}`, shipment)
        : await axios.put(`${API_URL}/api/orders/${orderId}/status?status=${newStatus}`);
      setOrders(orders.map(o =>
        o.id === orderId
          ? { ...o, status: newStatus,
              shipping_method: res.data.shipping_method ?? o.shipping_method,
              carrier: res.data.carrier ?? o.carrier,
              tracking_number: res.data.tracking_number ?? o.tracking_number,
              tracking_url: res.data.tracking_url ?? o.tracking_url }
          : o
      ));
      toast.success('Statut mis à jour');
    } catch (error) {
      // FastAPI 422 returns `detail` as an array of validation errors. We must
      // never pass that directly to toast.error — it would render an object.
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail) && detail.length
          ? (detail[0]?.msg || 'Données invalides')
          : 'Erreur lors de la mise à jour';
      toast.error(msg);
      throw error;
    }
  };

  const requestStatusChange = (orderId, newStatus) => {
    // Intercept "shipped" to capture carrier / tracking info first.
    if (newStatus === 'shipped') {
      setShipmentTarget(orderId);
      return;
    }
    updateStatus(orderId, newStatus);
  };

  const confirmShipment = async (shipment) => {
    if (!shipmentTarget) return;
    setSubmittingShipment(true);
    try {
      await updateStatus(shipmentTarget, 'shipped', shipment);
      setShipmentTarget(null);
    } catch {
      // toast already shown
    } finally {
      setSubmittingShipment(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-500 bg-yellow-500/10 border-yellow-500/30',
      processing: 'text-purple-500 bg-purple-500/10 border-purple-500/30',
      ready: 'text-blue-500 bg-blue-500/10 border-blue-500/30',
      shipped: 'text-cyan-500 bg-cyan-500/10 border-cyan-500/30',
      delivered: 'text-green-500 bg-green-500/10 border-green-500/30',
      cancelled: 'text-red-500 bg-red-500/10 border-red-500/30',
      preorder: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
      // Legacy
      confirmed: 'text-blue-500 bg-blue-500/10 border-blue-500/30',
    };
    return colors[status] || 'text-zinc-500 bg-zinc-500/10';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      processing: 'En préparation',
      ready: 'Terminée — en attente paiement',
      shipped: 'Envoyée',
      delivered: 'Livrée',
      cancelled: 'Annulée',
      preorder: 'Précommande (arrivage)',
      // Legacy
      confirmed: 'Terminée — en attente paiement',
    };
    return labels[status] || status;
  };

  const handleFulfillmentUpdate = (updatedOrder) => {
    setOrders(orders.map(o => o.id === updatedOrder.id ? updatedOrder : o));
  };

  const downloadOrderPDF = async (orderId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/orders/${orderId}/pdf`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bon-preparation-${orderId.slice(-8).toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Bon de préparation téléchargé');
    } catch {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const statuses = ['active', 'all', 'pending', 'processing', 'ready', 'shipped', 'delivered', 'preorder', 'cancelled'];

  const filteredOrders = (() => {
    if (statusFilter === 'all') return orders;
    if (statusFilter === 'active') return orders.filter(o => o.status === 'pending' || o.status === 'processing');
    return orders.filter(o => o.status === statusFilter);
  })();

  const activeCount = orders.filter(o => o.status === 'pending' || o.status === 'processing').length;

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <Link to="/admin" className="text-zinc-500 hover:text-white text-sm mb-2 inline-block">
              ← Retour au dashboard
            </Link>
            <h1 
              className="text-4xl text-white font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Gestion des Commandes
            </h1>
          </div>
          <button
            onClick={exportCSV}
            disabled={exporting}
            className="flex items-center gap-2 border border-[color:var(--color-primary)] text-[color:var(--color-primary)] px-4 py-2 hover:bg-[color:var(--color-primary)] hover:text-black transition-all text-sm disabled:opacity-50"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Export...' : 'Exporter CSV'}
          </button>
        </div>

        {/* Status Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          {statuses.map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 text-sm uppercase tracking-wider transition-colors ${
                statusFilter === status
                  ? 'bg-[color:var(--color-primary)] text-black'
                  : 'border border-white/10 text-zinc-400 hover:border-white/20'
              }`}
              data-testid={`filter-${status}`}
            >
              {status === 'all' ? 'Toutes' : status === 'active' ? `À traiter${activeCount > 0 ? ` (${activeCount})` : ''}` : getStatusLabel(status)}
            </button>
          ))}
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={`skeleton-${i}`} className="bg-[color:var(--color-surface)] border border-white/10 h-32 animate-pulse" />
            ))}
          </div>
        ) : filteredOrders.length > 0 ? (
          <div className="space-y-4" data-testid="orders-list">
            {filteredOrders.map(order => (
              <div 
                key={order.id} 
                className="bg-[color:var(--color-surface)] border border-white/10 p-6"
                data-testid={`order-${order.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-white">#{order.id.slice(-8).toUpperCase()}</h3>
                      <span className={`px-3 py-1 text-xs uppercase tracking-wider border ${getStatusColor(order.status)}`}>
                        {getStatusLabel(order.status)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-zinc-500 text-sm">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {new Date(order.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                    <button
                      type="button"
                      onClick={() => downloadOrderPDF(order.id)}
                      className="flex items-center gap-1 text-xs border border-white/10 text-zinc-400 hover:text-[color:var(--color-primary)] hover:border-[color:rgb(var(--primary-rgb)/0.30)] px-3 py-1.5 transition-colors"
                      data-testid={`pdf-order-${order.id}`}
                      title="Télécharger le bon de préparation PDF"
                    >
                      <FileText className="w-3 h-3" />
                      PDF
                    </button>
                    {isStaff && (
                      <WhatsAppNotifyButton
                        orderId={order.id}
                        status={order.status}
                        customerPhone={order.customer_phone}
                      />
                    )}
                    {isStaff && (
                      <button
                        type="button"
                        onClick={() => setEditingOrder(order)}
                        className="flex items-center gap-1 text-xs border border-white/10 text-zinc-400 hover:text-[color:var(--color-primary)] hover:border-[color:rgb(var(--primary-rgb)/0.30)] px-3 py-1.5 transition-colors"
                        data-testid={`edit-order-${order.id}`}
                      >
                        <Edit3 className="w-3 h-3" />
                        Préparer / Modifier
                      </button>
                    )}
                    <span 
                      className="text-2xl text-[color:var(--color-primary)] font-light"
                      style={{ fontFamily: 'Cormorant Garamond, serif' }}
                    >
                      {order.total.toFixed(2)}€
                    </span>
                  </div>
                </div>

                {/* Customer Info - personnel autorisé (permission orders) */}
                {isStaff && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4 border-t border-white/5">
                    <div>
                      <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Client</p>
                      <p className="text-white">{order.customer_name}</p>
                      <p className="text-zinc-400 text-sm">{order.customer_phone}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Adresse</p>
                      <p className="text-zinc-400 text-sm">{order.customer_address}</p>
                    </div>
                  </div>
                )}

                {isAdmin && <OrderProfitBadge orderId={order.id} />}

                {/* Order Items */}
                <div className="py-4 border-t border-white/5">
                  {/* Shipment info if present */}
                  {(order.shipping_method === 'carrier' || order.shipping_method === 'pickup') && (
                    <div className="mb-3 p-3 border border-cyan-500/20 bg-cyan-500/5 flex items-start gap-3" data-testid={`shipment-info-${order.id}`}>
                      {order.shipping_method === 'pickup' ? (
                        <Store className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                      ) : (
                        <Truck className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="text-xs">
                        {order.shipping_method === 'pickup' ? (
                          <span className="text-cyan-400">Retrait en boutique</span>
                        ) : (
                          <>
                            <span className="text-cyan-400">
                              Envoyé via <strong>{order.carrier}</strong>
                            </span>
                            <span className="text-zinc-400 ml-2 font-mono">
                              {order.tracking_number}
                            </span>
                            {order.tracking_url && (
                              <a href={order.tracking_url} target="_blank" rel="noopener noreferrer"
                                className="ml-3 text-[color:var(--color-primary)] hover:underline">
                                Suivre le colis →
                              </a>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  <p className="text-zinc-500 text-xs uppercase tracking-wider mb-3">Produits</p>
                  <div className="space-y-1.5">
                    {prepSortItems(order.items).map((item, idx, arr) => (
                      <div key={`${item.product_id}-${idx}`}>
                        {(idx === 0 || ((arr[idx - 1].brand || '').trim() || 'Autres') !== ((item.brand || '').trim() || 'Autres')) && (
                          <div className="flex items-center gap-2 mt-2 mb-1" data-testid={`order-brand-header-${order.id}-${idx}`}>
                            <span className="text-[color:var(--color-primary)] text-[10px] uppercase tracking-[0.25em]">
                              {(item.brand || '').trim() || 'Autres'}
                            </span>
                            <span className="flex-1 h-px bg-[color:rgb(var(--primary-rgb)/0.15)]" />
                          </div>
                        )}
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-400">
                            {item.product_name} x{item.quantity}
                            {item.category && <span className="text-zinc-600 ml-1">· {item.category}</span>}
                            {item.original_quantity && item.original_quantity !== item.quantity && (
                              <span className="text-amber-500 ml-1">(commandé: {item.original_quantity})</span>
                            )}
                            <span className="text-zinc-600 ml-1">({item.tier})</span>
                            {item.status === 'partial' && (
                              <span className="ml-2 text-xs px-1.5 py-0.5 border border-amber-500/30 text-amber-500 bg-amber-500/10">Partiel</span>
                            )}
                            {item.status === 'unavailable' && (
                              <span className="ml-2 text-xs px-1.5 py-0.5 border border-red-500/30 text-red-500 bg-red-500/10">Indisponible</span>
                            )}
                          </span>
                          <span className="text-white">{item.total.toFixed(2)}€</span>
                        </div>
                      </div>
                    ))}
                    {order.admin_note && (
                      <div className="mt-2 p-2 border border-amber-500/20 bg-amber-500/5 text-amber-400 text-xs">
                        Note: {order.admin_note}
                      </div>
                    )}
                    {order.promo_applied && (
                      <div className="flex justify-between text-sm text-[color:var(--color-primary)]">
                        <span>Code: {order.promo_applied.code}</span>
                        <span>-{order.promo_applied.discount_amount.toFixed(2)}€</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Actions */}
                <div className="pt-4 border-t border-white/5">
                  <p className="text-zinc-500 text-xs uppercase tracking-wider mb-3">Changer le statut</p>
                  {order.status === 'preorder' && (
                    <p className="text-amber-400 text-xs mb-3" data-testid={`preorder-hint-${order.id}`}>
                      Précommande — livraison prévue le {order.expected_date ? new Date(order.expected_date).toLocaleDateString('fr-FR') : 'à confirmer'}.
                      Validez la réception dans <Link to="/admin/precommandes" className="underline">Précommandes</Link> pour la passer en préparation.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {['pending', 'processing', 'ready', 'shipped', 'delivered', 'cancelled'].map(status => (
                      <button
                        key={status}
                        onClick={() => requestStatusChange(order.id, status)}
                        disabled={order.status === status || (order.status === 'preorder' && status !== 'cancelled')}
                        className={`px-3 py-1 text-xs transition-colors ${
                          order.status === status
                            ? 'bg-[color:var(--color-primary)] text-black'
                            : 'border border-white/10 text-zinc-500 hover:text-white hover:border-white/20'
                        }`}
                        data-testid={`status-${status}-${order.id}`}
                      >
                        {getStatusLabel(status)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-[color:var(--color-surface)] border border-white/10">
            <p className="text-zinc-500">Aucune commande {statusFilter !== 'all' ? `avec le statut "${getStatusLabel(statusFilter)}"` : ''}</p>
          </div>
        )}
      </div>

      {/* Fulfillment Modal */}
      {editingOrder && (
        <OrderFulfillmentModal
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onUpdated={handleFulfillmentUpdate}
        />
      )}

      {/* Shipment Info Modal */}
      <ShipmentInfoModal
        open={!!shipmentTarget}
        orderId={shipmentTarget}
        submitting={submittingShipment}
        onCancel={() => setShipmentTarget(null)}
        onConfirm={confirmShipment}
      />
    </div>
  );
};

export default AdminOrders;
